import { motion } from "framer-motion";
import { cn } from "src/lib/cn";
import { MenuPopoverItem, SudoMenuButton } from "src/sudokit/components/SudoMenuButton";
import { Text } from "src/sudokit/components/Text";
import ImageShimmerOverlay from "./ImageShimmerOverlay";
import SVG from "react-inlinesvg";
import { SetThumbnailMenuItem, TileMenuTrigger } from "./TileActionsMenu";
import { useFadeInImage } from "./lib/useFadeInImage";
import type { TileState } from "./lib/types";

const GRID_SLOTS = 4;
const TILE_GLINT_DELAYS_SEC = [0, 0.4, 0.8, 1.2];

export default function ImageResultGrid({
  tiles,
  coverImageId,
  settingCoverImageId,
  canGenerate,
  onRetry,
  onSetThumbnail,
  onUseReference,
  aspectRatio = "1:1",
  imageNoun,
}: {
  tiles: TileState[];
  coverImageId: string | null;
  settingCoverImageId: string | null;
  canGenerate: boolean;
  onRetry: (stylePresetId: string) => void;
  onSetThumbnail: (imageId: string) => void;
  onUseReference?: (tile: TileState) => void;
  /** "W:H"; tiles take their height from the container and their width from this ratio. */
  aspectRatio?: string;
  imageNoun: string;
}) {
  const isSingleTile = tiles.length === 1;
  const isPair = tiles.length === 2;
  const [ratioWidth, ratioHeight] = aspectRatio.split(":");
  const tileStyle = {
    aspectRatio: `${ratioWidth} / ${ratioHeight}`,
    // A pair sits in one row, so each tile is capped by half the stage width as well as its height
    // (cq units resolve against the row, which is a size container).
    ...(isPair ? { height: `min(100cqh, calc((50cqw - 5px) * ${ratioHeight} / ${ratioWidth}))` } : {}),
  };

  const renderedTiles = tiles.map((tile, index) => {
    const isCover = !!tile.imageId && tile.imageId === coverImageId;
    const canAct = tile.status === "done" && !tile.saveFailed && !!tile.imageId;
    const isSettingCover = !!tile.imageId && settingCoverImageId === tile.imageId;
    return (
      <div
        key={tile.stylePresetId}
        style={tileStyle}
        className={cn(
          "group relative box-border shrink-0 overflow-hidden rounded-2xl",
          // Utilities are !important in this app's Tailwind config, so h-full would override the pair's inline height.
          !isPair && "h-full",
          tile.status === "done" ? "bg-app-menu-color" : "bg-app-grey-3",
        )}
      >
        {tile.status === "loading" && <ImageShimmerOverlay delaySeconds={TILE_GLINT_DELAYS_SEC[index]} />}
        {tile.status === "done" && tile.imageUrl && (
          <>
            <ImageResultTile
              imageUrl={tile.imageUrl}
              alt={tile.stylePresetLabel}
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-4 pb-4 pt-10 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100">
              <span className="block text-left text-xs font-normal leading-none text-white">{tile.stylePresetLabel}</span>
            </div>
            {canAct && (
              <div className="!absolute right-2 top-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100 has-[[aria-expanded=true]]:opacity-100">
                <SudoMenuButton
                  trigger={<TileMenuTrigger aria-label={`${tile.stylePresetLabel} ${imageNoun} actions`} />}
                  side="bottom"
                  align="end"
                  sideOffset={4}
                  menuAriaLabel={`${tile.stylePresetLabel} ${imageNoun} actions`}
                  modal={false}
                  menuPortalClassName="z-[1000000000]"
                >
                  <SetThumbnailMenuItem
                    busy={isSettingCover}
                    disabled={settingCoverImageId !== null && !isCover}
                    isThumbnail={isCover}
                    onSelect={() => onSetThumbnail(tile.imageId!)}
                  />
                  {onUseReference && (
                    <MenuPopoverItem
                      onSelect={() => onUseReference(tile)}
                      className="gap-2 whitespace-nowrap"
                    >
                      <SVG
                        src="/assets/image.svg"
                        className="size-4 shrink-0"
                      />
                      Use reference
                    </MenuPopoverItem>
                  )}
                </SudoMenuButton>
              </div>
            )}
          </>
        )}
        {tile.status === "done" && tile.saveFailed && (
          <div className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-between gap-2 bg-[color-mix(in_srgb,var(--danger-color)_75%,black)] px-2 py-1.5">
            <Text
              size="sm"
              className="!text-[10px] leading-tight text-white"
            >
              Not saved
            </Text>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRetry(tile.stylePresetId);
              }}
              className="cursor-pointer appearance-none rounded-full border-0 bg-white px-2.5 py-1 text-[10px] font-semibold text-app-danger-color"
            >
              Retry
            </button>
          </div>
        )}
        {tile.status === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-[color-mix(in_srgb,var(--danger-color)_10%,transparent)] p-2 text-center">
            <Text
              size="sm"
              className="!text-[11px] leading-tight text-app-danger-color"
            >
              {tile.error || "Couldn't generate"}
            </Text>
            <button
              type="button"
              disabled={!canGenerate}
              onClick={(e) => {
                e.stopPropagation();
                if (!canGenerate) return;
                onRetry(tile.stylePresetId);
              }}
              className={cn(
                "appearance-none rounded-full border-0 bg-app-danger-color px-2.5 py-1 text-[10px] font-semibold text-app-cta-text-color",
                canGenerate ? "cursor-pointer" : "cursor-not-allowed opacity-50",
              )}
            >
              Retry
            </button>
          </div>
        )}
      </div>
    );
  });

  if (isSingleTile) {
    return <div className="flex h-full w-full items-center justify-center">{renderedTiles}</div>;
  }

  if (isPair) {
    return <div className="flex h-full w-full items-center justify-center gap-[10px] [container-type:size]">{renderedTiles}</div>;
  }

  // Unfilled slots keep the 2x2 footprint while fewer styles than four were requested.
  const slots = [
    ...renderedTiles,
    ...Array.from({ length: Math.max(0, GRID_SLOTS - tiles.length) }, (_, i) => (
      <div
        key={`empty-${i}`}
        aria-hidden="true"
        style={tileStyle}
        className="box-border h-full shrink-0 rounded-2xl bg-app-grey-4 opacity-60"
      />
    )),
  ];

  // Flex rows of a definite height (not CSS grid) so each tile's width resolves from its
  // aspect-ratio; grid sizes columns before rows and would collapse them.
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-[10px]">
      <div className="flex h-[calc(50%-5px)] gap-[10px]">{slots.slice(0, 2)}</div>
      <div className="flex h-[calc(50%-5px)] gap-[10px]">{slots.slice(2, 4)}</div>
    </div>
  );
}

function ImageResultTile({ imageUrl, alt }: { imageUrl: string; alt: string }) {
  const { loaded, ref, onLoad } = useFadeInImage(imageUrl);
  return (
    <motion.div
      className="box-border h-full w-full"
      initial={{ opacity: 0, scale: 1.06, filter: "blur(3px)" }}
      animate={loaded ? { opacity: 1, scale: 1, filter: "blur(0px)" } : { opacity: 0, scale: 1.06, filter: "blur(3px)" }}
      transition={{ duration: 0.45, ease: [0.22, 0.9, 0.32, 1] }}
    >
      <img
        ref={ref}
        src={imageUrl}
        alt={alt}
        decoding="async"
        onLoad={onLoad}
        className="block h-full w-full rounded-2xl object-cover"
      />
    </motion.div>
  );
}
